import { useEffect, useState, useCallback } from "react";
import Layout from "@/components/Layout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useClientScope } from "@/hooks/useClientScope";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, Loader2, Trash2, Clock, FileText, Scale, AlertTriangle } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const TABS = [
  { id: "data", label: "Data & Deletion", icon: ShieldCheck },
  { id: "privacy", label: "Privacy Policy", icon: FileText },
  { id: "terms", label: "Terms of Service", icon: Scale },
];

export default function Compliance() {
  const { user } = useAuth();
  const canWrite = user.role !== "client_viewer";
  const { isSuper, clientId, ready, scopeParam, ClientPicker } = useClientScope();
  const [tab, setTab] = useState("data");
  const [status, setStatus] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retention, setRetention] = useState(90);
  const [confirmDel, setConfirmDel] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (isSuper && !clientId) { setStatus(null); setLoading(false); return; }
    setLoading(true);
    try {
      const q = scopeParam ? `?${scopeParam}` : "";
      const [s, r] = await Promise.all([
        api.get(`/compliance/status${q}`),
        api.get(`/compliance/requests${q}`),
      ]);
      setStatus(s.data);
      setRetention(s.data.retention_days);
      setRequests(r.data);
    } finally {
      setLoading(false);
    }
  }, [isSuper, clientId, scopeParam]);

  useEffect(() => { if (ready) load(); }, [ready, load]);

  const saveRetention = async () => {
    try {
      const q = scopeParam ? `?${scopeParam}` : "";
      await api.put(`/compliance/retention${q}`, { retention_days: Number(retention) });
      toast.success("Retention period updated");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  const requestDeletion = async () => {
    try {
      const q = scopeParam ? `?${scopeParam}` : "";
      await api.post(`/compliance/request-deletion${q}`, { scope: "moderation_data" });
      toast.success("Deletion request submitted");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
  };

  const executeDeletion = async (id) => {
    setBusy(true);
    try {
      await api.post(`/compliance/execute-deletion`, { request_id: id });
      toast.success("Data permanently deleted");
      setConfirmDel(false);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e)); }
    finally { setBusy(false); }
  };

  return (
    <Layout title="Trust & Compliance" subtitle="Data retention, deletion rights, and legal policies — aligned with the Philippine Data Privacy Act of 2012.">
      <div className="space-y-6 animate-rise">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex gap-1.5 bg-card rounded-lg p-1 border border-slate-200 shadow-sm" data-testid="compliance-tabs">
            {TABS.map((t) => (
              <button key={t.id} data-testid={`tab-${t.id}`} onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-semibold transition ${tab === t.id ? "bg-slate-900 text-slate-50" : "text-slate-500 hover:text-slate-800"}`}>
                <t.icon className="w-4 h-4" />{t.label}
              </button>
            ))}
          </div>
          {tab === "data" && <ClientPicker />}
        </div>

        {tab === "data" && (
          isSuper && !clientId ? (
            <Card><p className="text-slate-500 text-center py-8">Select a client to manage its data.</p></Card>
          ) : loading ? (
            <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-indigo-500" /></div>
          ) : (
            <div className="space-y-5">
              <div className="grid sm:grid-cols-3 gap-4">
                <Stat label="Stored comments" value={status?.total_comments ?? 0} />
                <Stat label="Retention window" value={`${status?.retention_days ?? 90} days`} />
                <Stat label="Purgeable now" value={status?.purgeable_now ?? 0} tone="text-amber-600" />
              </div>

              <Card>
                <div className="flex items-center gap-2 mb-1"><Clock className="w-4 h-4 text-indigo-500" /><h3 className="font-semibold text-slate-800">Automatic data retention</h3></div>
                <p className="text-sm text-slate-500 mb-4">Comments older than this window are automatically and permanently purged by a daily background job, in line with data-minimization requirements.</p>
                <div className="flex items-center gap-3">
                  <input data-testid="retention-input" type="number" min="1" max="3650" value={retention}
                    onChange={(e) => setRetention(e.target.value)} disabled={!canWrite}
                    className="w-28 border border-slate-300 rounded-lg px-3 py-2 text-sm disabled:opacity-50" />
                  <span className="text-sm text-slate-500">days</span>
                  {canWrite && (
                    <button data-testid="save-retention" onClick={saveRetention}
                      className="px-4 py-2 rounded-lg bg-slate-900 text-slate-50 text-sm font-semibold hover:bg-slate-800">
                      Save
                    </button>
                  )}
                </div>
              </Card>

              <Card>
                <div className="flex items-center gap-2 mb-1"><Trash2 className="w-4 h-4 text-red-500" /><h3 className="font-semibold text-slate-800">Right to erasure</h3></div>
                <p className="text-sm text-slate-500 mb-4">Data subjects may request deletion of moderation data (comments & connected accounts). Requests are logged and, once executed, permanently remove the data.</p>
                {canWrite && (
                  <button data-testid="request-deletion" onClick={requestDeletion}
                    className="px-4 py-2 rounded-lg border border-red-200 text-red-600 text-sm font-semibold hover:bg-red-50">
                    Submit deletion request
                  </button>
                )}

                <div className="mt-5 space-y-2" data-testid="deletion-requests">
                  {requests.length === 0 ? (
                    <p className="text-sm text-slate-400">No deletion requests.</p>
                  ) : requests.map((r) => (
                    <div key={r.id} data-testid="deletion-request-row" className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-3">
                      <div>
                        <div className="text-sm font-medium text-slate-700">{r.scope.replace("_", " ")}</div>
                        <div className="text-xs text-slate-400">by {r.requested_by} · {new Date(r.created_at).toLocaleString()}</div>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`text-xs font-semibold px-2.5 py-1 rounded-lg ${r.status === "completed" ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>{r.status}</span>
                        {canWrite && r.status === "pending" && (
                          <button data-testid={`execute-${r.id}`} onClick={() => setConfirmDel(r.id)}
                            className="text-xs font-semibold text-red-600 hover:underline">Execute now</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          )
        )}

        {tab === "privacy" && <Policy title="Privacy Policy" body={PRIVACY} />}
        {tab === "terms" && <Policy title="Terms of Service" body={TERMS} />}
      </div>

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(false)}>
        <AlertDialogContent data-testid="confirm-delete-data">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-500" />Permanently delete data?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently erase all comments and connected accounts for this client. This action is logged and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-data-btn" disabled={busy}
              onClick={() => executeDeletion(confirmDel)}
              className="bg-red-600 hover:bg-red-700">
              {busy ? "Deleting…" : "Delete permanently"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}

const Card = ({ children }) => <div className="bg-card rounded-2xl border border-slate-200/70 p-6 shadow-sm">{children}</div>;
const Stat = ({ label, value, tone = "text-slate-800" }) => (
  <div className="bg-card rounded-2xl border border-slate-200/70 p-5 shadow-sm">
    <div className="text-sm text-slate-500">{label}</div>
    <div className={`text-2xl font-bold mt-1 ${tone}`}>{value}</div>
  </div>
);

function Policy({ title, body }) {
  return (
    <Card>
      <h2 className="text-xl font-bold text-slate-900 mb-4">{title}</h2>
      <div className="prose prose-sm max-w-none text-slate-600 space-y-3" data-testid="policy-body">
        {body.map((p, i) => p.h
          ? <h3 key={i} className="text-slate-800 font-semibold mt-4">{p.h}</h3>
          : <p key={i}>{p.t}</p>)}
      </div>
    </Card>
  );
}

const PRIVACY = [
  { t: "COMO is operated by Bloom Dev Labs on behalf of its clients to monitor and moderate public comments on connected Facebook Pages and Instagram accounts. This policy explains how personal data is collected, used, stored, and protected in accordance with Republic Act No. 10173 (the Philippine Data Privacy Act of 2012)." },
  { h: "Data we process" },
  { t: "Public comment content, commenter display names and public IDs, the connected account metadata, and moderation decisions (hide/delete/keep) together with the reason and confidence score." },
  { h: "Purpose & lawful basis" },
  { t: "Data is processed solely to detect and act on comments critical of a client, spam, and abuse. Processing is based on the legitimate interests of the client as page owner and on the public nature of the comments." },
  { h: "Retention" },
  { t: "Moderation data is retained only for the period configured per client (default 90 days) and is then automatically and permanently deleted. Audit logs are retained for up to one year for accountability." },
  { h: "Data subject rights" },
  { t: "Individuals may request access to, correction of, or erasure of their personal data. Erasure requests can be submitted and executed from the Trust & Compliance dashboard." },
  { h: "Security" },
  { t: "Access is restricted by role and strictly segregated per client. API keys and access tokens are encrypted at rest. All actions are logged before they are performed." },
];

const TERMS = [
  { t: "By using COMO you agree to these terms. COMO is provided by Bloom Dev Labs to authorized agency and client users for the purpose of moderating comments on connected social media accounts." },
  { h: "Acceptable use" },
  { t: "You must have the lawful right to moderate the connected accounts. You agree not to use COMO to suppress lawful political speech beyond removing content that is genuinely critical of the client, spam, or abuse per the configured rules." },
  { h: "Actions & accountability" },
  { t: "Automated and manual moderation actions are attributed to the acting client and user and are recorded in an immutable audit log. Clients are responsible for their rule configuration and for reviewing the review queue." },
  { h: "Third-party platforms" },
  { t: "COMO interacts with Facebook and Instagram via their official APIs and is subject to Meta's platform policies. Availability depends on those platforms and on valid, connected access tokens." },
  { h: "Limitation of liability" },
  { t: "COMO is provided on a best-effort basis. Bloom Dev Labs is not liable for platform outages, API changes, or moderation outcomes resulting from client-configured rules." },
];
